import { useCallback, useContext, useEffect, useState } from "react";

import { RootContext } from "../context/root-provider";
import {
  RTMS_STATUS,
  onRtmsStatusChange,
  pauseRtms,
  readRtmsStatus,
  readRunningMeetingDoc,
  resumeRtms,
  stopRtms,
  supportedApis,
  supportsApi,
  timestampMs,
} from "../services/zoomMeeting";

// The meeting's RTMS stream, for the doc it streams into (TT-1207 F2): what
// Zoom reports, its clock, and the controls. The bar renders it, the way
// HostCaptureBar renders useHostCapture.
//
// Zoom owns the stream. The status is read once and then followed through
// Zoom's events; a stream belongs to the participant who started it, so Zoom
// refuses a pause or stop from anyone else, and `error` carries its answer.
// A client too old to report status (getRTMSStatus needs 6.5.5) reads as
// live, with stop alone.
export default function useZoomRtms(docId) {
  const { userInfo, inMeeting } = useContext(RootContext);
  const userId = userInfo?.username;

  // null until the reads land; then whether this doc is the meeting's stream
  // and whether the client can pause it.
  const [stream, setStream] = useState(null);
  // null when Zoom cannot report; else one of RTMS_STATUS.
  const [status, setStatus] = useState(null);
  const [starter, setStarter] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const applySession = useCallback((session) => {
    if (!session) {
      setStatus(null);
      return;
    }
    setStatus(session.status);
    if (session.displayName) setStarter(session.displayName);
    if (session.status === RTMS_STATUS.STARTED) {
      setStartedAt((prev) => prev ?? timestampMs(session.timestamp) ?? Date.now());
    }
  }, []);

  useEffect(() => {
    if (!inMeeting || !userId || !docId) {
      setStream(null);
      return undefined;
    }
    let active = true;
    // Listening from the start, so no change slips by between the read and
    // the subscription.
    const off = onRtmsStatusChange((session) => {
      if (active) applySession(session);
    });
    (async () => {
      const [doc, apis] = await Promise.all([readRunningMeetingDoc(userId), supportedApis()]);
      if (!active) return;
      if (doc?.docId !== docId) {
        setStream({ owned: false, pauseSupported: false });
        return;
      }
      let session = null;
      try {
        session = await readRtmsStatus();
      } catch (err) {
        console.log("getRTMSStatus failed; showing the stream as live:", err);
      }
      if (!active) return;
      applySession(session);
      setStream({ owned: true, pauseSupported: supportsApi(apis, "pauseRTMS") });
    })();
    return () => {
      active = false;
      off();
    };
  }, [inMeeting, userId, docId, applySession]);

  // The clock runs from the stream's start until it stops; a pause shows as
  // the status, not as a stopped clock, the way a meeting's own clock does.
  useEffect(() => {
    if (!startedAt || status === RTMS_STATUS.STOPPED) return undefined;
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [startedAt, status]);

  // A control's outcome: Zoom's events report the new status on 6.5.10+; an
  // older client gets it from the call having succeeded. A client that
  // cannot report status stays "cannot report".
  const act = useCallback(async (call, became) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      setStatus((prev) => (prev === null ? prev : became));
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  const pause = useCallback(() => act(pauseRtms, RTMS_STATUS.PAUSED), [act]);
  const resume = useCallback(() => act(resumeRtms, RTMS_STATUS.STARTED), [act]);
  const stop = useCallback(() => act(stopRtms, RTMS_STATUS.STOPPED), [act]);

  return {
    active: Boolean(stream?.owned),
    pauseSupported: Boolean(stream?.pauseSupported),
    status,
    starter,
    elapsed,
    busy,
    error,
    pause,
    resume,
    stop,
  };
}
